'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

import { PhoneInput } from '@/components/ui/phone-input'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { mapFormToApiSubmission } from '@/api/utils'
import { CONTACT_US_FORM, createSubmission } from '@/lib/public-api'
import React from 'react'
import { trackEvent } from '@/lib/gtm'
import Link from 'next/link'
import { Checkbox } from '@/components/ui/checkbox'

const formSchema = z.object({
  fullName: z.string().min(2, { message: 'Please enter your full name.' }),
  phoneNumber: z.string(),
  email: z.string().email({ message: 'Please enter a valid email address.' }),
  message: z.string().optional(),
  consent: z.boolean().refine((v) => v === true, {
    message: 'You must agree to sign up for text messages to submit.',
  }),
})

export function ContactForm() {
  const [success, setSuccess] = React.useState(false)
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema as any),
    defaultValues: {
      fullName: '',
      phoneNumber: '',
      email: '',
      message: '',
      consent: false,
    },
  })
  async function onSubmit(data: any) {
    const submission = mapFormToApiSubmission(data)
    try {
      await createSubmission(CONTACT_US_FORM, submission)
      setSuccess(true)
      trackEvent('form_submitted', {
        form: 'contact',
      })
    } catch (e) {
      console.error(e)
    }
  }

  if (success) {
    return (
      <Card className='rounded-2xl max-w-[500px] mx-auto'>
        <CardHeader className='text-center'>
          <CardTitle className='text-2xl font-semibold leading-none tracking-tight'>
            Thanks for reaching out
          </CardTitle>
          <CardDescription>
            We&apos;ve received your inquiry and we&apos;ll be in touch soon.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            style={{
              width: '100%',
              height: 0,
              paddingBottom: '75%',
              position: 'relative',
            }}
          >
            <iframe
              title='Send us a message'
              src='https://giphy.com/embed/XreQmk7ETCak0'
              width='100%'
              height='100%'
              style={{ position: 'absolute' }}
              frameBorder={0}
              className='giphy-embed'
            />
          </div>
        </CardContent>
      </Card>
    )
  }
  return (
    <Card className='rounded-2xl max-w-[500px] mx-auto'>
      <CardHeader>
        <CardTitle className='text-2xl font-semibold leading-none tracking-tight'>
          Send us a message
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
            <FormField
              control={form.control}
              name='fullName'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Full name<span className='text-destructive'> *</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Business email<span className='text-destructive'> *</span>
                  </FormLabel>
                  <FormControl>
                    <Input type='email' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='phoneNumber'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Mobile number<span className='text-destructive'> *</span>
                  </FormLabel>
                  <FormControl>
                    <PhoneInput hideDropdown {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='message'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Let us know how we can help</FormLabel>
                  <FormControl>
                    <Textarea className='resize-none' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='consent'
              render={({ field }) => (
                <FormItem className='flex flex-row items-start !mb-4 space-x-3 space-y-0'>
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className='space-y-1 leading-none'>
                    <FormLabel>Respond to me via text message and sign me up for texts</FormLabel>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
            <Button
              type='submit'
              size='lg'
              className='w-full'
              loading={form.formState.isSubmitting}
            >
              Submit
            </Button>
            <p className='text-[11px] text-muted-foreground'>
              By entering your mobile number and checking &quot;Respond to me via text message&quot;
              you agree to receive marketing and promotional messages and information from Helios
              CRM, Inc at the number provided. Message and data rates may apply. Message frequency
              varies. Reply HELP for help or STOP to cancel. View our{' '}
              <Link href='/terms-of-use' className='underline'>
                Terms of Use
              </Link>{' '}
              and{' '}
              <Link href='/privacy-policy' className='underline'>
                Privacy Policy
              </Link>
              .
            </p>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
